/**
 * This is the main module page for MiniAppSDK usage.
 * This module has an only main class, {@link MiniAppSDK}, acting as
 * an interface for developers to use any features or APIs
 *
 *
 * @example
 * Firstly, we have to initialize the SDK by the following code
 * ```ts
 * // to initialize the SDK
 * await MiniAppSDK.init({ appId: [app-id] });
 * ```
 *
 * Noted that `app-id` and `api-key` should be replaced by the value getting from super app provider.
 *
 * Once the initialization above is successful. We could use any APIs inside {@link MiniAppSDK}
 *
 * @packageDocumentation
 */
import * as yup from 'yup'

import ARSceneConfiguration from './ar-scene'
import AssetCollection from './asset'
import Bridge from './bridge'
import getConfig, { AppConfig, ISSConfig } from './config'
import {
  BridgeError,
  MiniAppARSceneError,
  MiniAppCoreError,
} from './config/error'
import { AllmapService, SuperAppUserService } from './services'
import {
  isEmpty,
  getUserIdFromScope,
} from './utils'
import PayloadValidator from './utils/payload-validator'

import decodeJWT from './utils/jwt-decode'

import {
  PermissionType,
  LoginProvider,
  Environment,
  UserLevel,
  EnvironmentDisplay,
} from './types/public'

/**
 * Class representing MiniAppSDK.
 * This class has no constructor, could not be initialized by `new` operator.
 * However, We provide {@link MiniAppSDK.init} method for initialize the SDK.
 * @class
 */
class MiniAppSDK {
  private static appId: string
  protected static bridge: Bridge
  private static allmapService: AllmapService
  private static superAppUserService: SuperAppUserService
  private static config: typeof AppConfig
  public static platform: MobilePlatform
  protected static mobileEvents = new Map<BindNativeEvents, Function>()
  public static version: string = AppConfig.VERSION
  public static commitId = AppConfig.COMMIT_ID

  private static environment: Environment
  private static exchangeTokenData: ExchangeTokenPayload
  private static exchangeTokenJWT: JWTDecode | undefined

  /**
   * This callbacks property is intended not to be used by any case.
   * It's intended to be used as a communication channel with mobile native handler.
   * So It must be public and global to be available for the communication.
   *
   * @hidden
   */
  public static callbacks: Map<string, (res: NativeResponse) => void> =
    new Map()
  public static assetCollection: AssetCollection
  public static arSceneConfiguration: ARSceneConfiguration
  public static PermissionType = PermissionType
  public static LoginProvider = LoginProvider

  static ARScene: typeof ARScene

  /**
   * Initialize Mini App SDK
   *
   * @example
   * ```ts
   * await MiniAppSDK.init({ appId: "[app-id]" });
   * ```
   *
   * @param {@link MiniAppSDKInitParams} option - an option object.
   *
   * @group Initialization Method
   *
   * @throws CANNOT_PARSE_MOBILE_RESPONSE
   */
  public static async init(option: MiniAppSDKInitParams) {
    // APP_ENV is replaced (not assign) with value passed from build.js
    const { appId, isDebug } = option

    // @ts-ignore

    this.appId = appId

    this.bridge = new Bridge({
      appId,
      version: this.version,
      timeout: AppConfig.BRIDGE_TIMEOUT,
      callbacks: this.callbacks,
      isDebug,
    })

    try {
      await this.checkCompatibility()
    } catch (error: any) {
      if (
        !AppConfig.CHECK_COMPATIBILITY_SOFT_ERROR_CODES.includes(
          error.errorCode
        )
      ) {
        throw error
      }
    }

    const token = await this.exchangeToken(true)
    if (!token) {
      throw new MiniAppCoreError('NOT_FOUND_TOKEN')
    }

    this.environment = await this.getEnvironment()
    if (this.environment === Environment.UNKNOWN) {
      throw new MiniAppCoreError("UNKNOWN_ENVIRONMENT");
    }
    console.log('🔥  MiniAppSDK  environment:', this.environment)

    // switch API based on environment
    this.config = getConfig(this.environment)

    const scopes = token.scope.split(' ')
    this.bridge.setMiniAppScope(scopes)

    this.assetCollection = new AssetCollection()
    this.arSceneConfiguration = new ARSceneConfiguration(this.assetCollection)

    // initialize all services
    this.superAppUserService = new SuperAppUserService({
      baseURL: this.config.GATEWAY_BASE_URL,
      timeout: 5000,
      appId: this.appId,
    })

    // call directly to allmap because wait for allmap gateway
    this.allmapService = new AllmapService({
      baseURL: this.config.GATEWAY_BASE_URL,
      timeout: 5000,
      appId: this.appId,
    })
    return appId
  }

  private static async getEnvironment(): Promise<Environment> {
    const iss: string = this.exchangeTokenJWT?.payload?.iss || ''
    if (!iss) {
      throw new MiniAppCoreError("TOKEN_ISS_NOT_FOUND")
    }

    let environment: Environment
    switch (iss) {
      case ISSConfig.PRODUCTION:
        environment = Environment.PRODUCTION
        break;
      case ISSConfig.STAGING:
        environment = Environment.STAGING
        break;
      case ISSConfig.DEVELOPMENT:
        environment = Environment.DEVELOPMENT
      default:
        environment = Environment.UNKNOWN
        break;
    }

    return environment
  }

  public static getAssetCollection() {
    return this.assetCollection
  }

  /**
   * Get user's muid.
   * @group Services Methods
   * @returns {Promise<string>}
   * @example
   * ```ts
   * const muid = await MiniAppSDK.getMuid();
   * console.log(muid);
   * ```
   */
  public static async getMuid(): Promise<string> {
    return this.exchangeToken().then((response) => {
      return getUserIdFromScope(response.scope)
    })
  }

  /**
   * Get allMemberId.
   * @group Services Methods
   * @returns {Promise<string>}
   * @example
   * ```ts
   * const allMemberId = await MiniAppSDK.getAllMemberId();
   * console.log(allMemberId);
   * ```
   */
  public static async getAllMemberId(): Promise<string> {
    let allMemberId: string = ''
    try {
      const { data } = await this.superAppUserService.getProfile()
      allMemberId = data.allMemberId || ''
    } catch (error) {
      console.log('cannot get profile', error)
    }
    return allMemberId
  }

  /**
   * Get user's profile.
   * @group Services Methods
   * @returns {Promise<Profile>}
   * @example
   * ```ts
   * const profile = await MiniAppSDK.getProfile();
   * console.log(profile);
   * ```
   */
  public static async getProfile(): Promise<Profile> {
    let profileImageUrl: string = ''
    let firstName: string = ''
    let lastName: string = ''
    let allMemberId: string = ''
    let mobileNo: string = ''
    let isTrueMoneyWalletBinded: boolean | undefined = undefined
    const bridgeUserLevel = await this.bridge
      .getUserStatus()
      .then((response) => response.userLevel)
      .catch(() => {
        console.error('cannot get user status')
        return UserLevel.GUEST
      })

    try {
      if (bridgeUserLevel === UserLevel.ALLMEMBER || bridgeUserLevel === UserLevel.CUSTOMER) {
        const { data } = await this.superAppUserService.getProfile()
        firstName = data.firstName || ''
        lastName = data.lastName || ''
        allMemberId = data.allMemberId || ''
        mobileNo = data.mobileNo || ''
        if (typeof data.isTrueMoneyWalletBinded !== 'undefined') {
          isTrueMoneyWalletBinded = data.isTrueMoneyWalletBinded
        }
      }
    } catch (error) {
      console.error('cannot get profile', error)
    }

    profileImageUrl = await this.bridge
      .getUserProfileImage()
      .then((response) => response.imageUrl)
      .catch((error) => {
        console.error('cannot get profile image', error)
        return ''
      })

    const muid = await this.getMuid()

    const profile: Profile = {
      muid,
      userLevel: bridgeUserLevel,
    }

    if (firstName) profile.firstName = firstName
    if (lastName) profile.lastName = lastName
    if (allMemberId) profile.allMemberId = allMemberId
    if (mobileNo) profile.mobileNo = mobileNo
    if (profileImageUrl) profile.profileImageUrl = profileImageUrl
    if (typeof isTrueMoneyWalletBinded !== 'undefined')
      profile.isTrueMoneyWalletBinded = isTrueMoneyWalletBinded

    return profile
  }

  /**
   * Get user's member point.
   * @group Services Methods
   * @returns {Promise<AllMemberPoint>}
   * @example
   * ```ts
   * const memberPoint = await MiniAppSDK.getAllMemberPoint();
   * console.log(memberPoint);
   * ```
   */
  public static async getAllMemberPoint(): Promise<AllMemberPoint> {
    const point = await this.superAppUserService
      .getAllMemberPoint()
      .then((response) => response.data)
    return point
  }

  /**
   * Open coupon menu in seven app.
   * @group App Invoked Methods
   */
  public static async openMyCoupon(): Promise<void> {
    const coupons = this.bridge.openMyCoupon()
    return coupons
  }

  /**
   * Close current running mini app.
   * @group App Invoked Methods
   *
   * @example
   * ```ts
   * await window.MiniAppSDK.closeMiniApp();
   * ```
   */
  public static async closeMiniApp(): Promise<void> {
    return this.bridge.closeWebView()
  }

  /**
   * Reload current running mini app.
   * @group App Invoked Methods
   */
  public static async reloadWebView(): Promise<void> {
    return this.bridge.reloadWebView()
  }

  /**
   * Open register all member page in seven app.
   * @group App Invoked Methods
   *
   * @example
   * ```ts
   * await window.MiniAppSDK.registerAllMember();
   * ```
   */
  public static async registerAllMember(): Promise<any> {
    const userLevelBefore = await this.bridge
      .getUserStatus()
      .then((response) => response.userLevel)

    try {
      const registeredResponse = await this.bridge.registerAllMember()
      await this.exchangeToken(true)
      return registeredResponse
    } catch (error) {
      const userLevelAfter = await this.bridge
        .getUserStatus()
        .then((response) => response.userLevel)

      if (userLevelBefore !== userLevelAfter) {
        await this.exchangeToken(true)
      }
      throw error
    }
  }

  /**
   * Open register customer page in seven app.
   * @group App Invoked Methods
   *
   *   * @example
   * ```ts
   * await window.MiniAppSDK.registerCustomer();
   * ```
   */
  public static async registerCustomer(): Promise<any> {
    const userLevelBefore = await this.bridge
      .getUserStatus()
      .then((response) => response.userLevel)

    try {
      const registeredResponse = await this.bridge.registerCustomer()
      await this.exchangeToken(true)
      return registeredResponse
    } catch (error) {
      const userLevelAfter = await this.bridge
        .getUserStatus()
        .then((response) => response.userLevel)

      if (userLevelBefore !== userLevelAfter) {
        await this.exchangeToken(true)
      }
      throw error
    }
  }

  /**
   * Open register customer page in seven app.
   * @group App Invoked Methods
   *
   *   * @example
   * ```ts
   * await window.MiniAppSDK.register7App();
   * ```
   */
  public static async register7App(): Promise<any> {
    const userLevelBefore = await this.bridge
      .getUserStatus()
      .then((response) => response.userLevel)

    try {
      const registeredResponse = await this.bridge.register7App()
      await this.exchangeToken(true)
      return registeredResponse
    } catch (error) {
      const userLevelAfter = await this.bridge
        .getUserStatus()
        .then((response) => response.userLevel)

      if (userLevelBefore !== userLevelAfter) {
        await this.exchangeToken(true)
      }
      throw error
    }
  }

  /**
   * Get user's current location.
   * @group Hardware Invoked Methods
   */
  public static async getCurrentLocation(): Promise<GPSLocation> {
    return this.bridge.getCurrentLocation()
  }

  /**
   * track analytical events
   *
   * @group Analytic methods
   *
   * @example
   * ```ts
   * window.MiniAppSDK.trackAnalytic("eventName", params);
   * ```
   */
  public static trackAnalytic(eventName: string, params: any): Promise<void> {
    return this.bridge.trackAnalytic(eventName, params)
  }

  /**
   * Open camera and scan for QRCode or barcode.
   * @group Hardware Invoked Methods
   *
   * @example
   * ```ts
   * const code = await MiniAppSDK.scanCode();
   * console.log(code); // => '09384290384'
   * ```
   */
  public static async scanCode(): Promise<string> {
    const { code } = await this.bridge.scanCode()

    return code
  }

  /**
   * Play the sound by assetId
   *
   * @param {@link assetId}
   * @param {@link PlaySoundOptions} option - an option object.
   *
   * @group Hardware Invoked Methods
   *
   *
   * @example
   * ```ts
   * await window.MiniAppSDK.playSound(soundId, options);
   * ```
   *
   */
  public static async playSound(
    assetId: string,
    options?: PlaySoundOptions
  ): Promise<unknown> {
    const hasSound = this.assetCollection.hasSoundExists(assetId)
    if (!hasSound) {
      throw new MiniAppCoreError('NOT_FOUND_ASSET_ID')
    } else {
      return this.bridge.playSound(assetId, options)
    }
  }

  /**
   * Stop the sound by assetId
   *
   * @param {@link assetId}
   *
   * @group Hardware Invoked Methods
   *
   * @example
   * ```ts
   * await window.MiniAppSDK.stopSound(soundId);
   * ```
   */
  public static async stopSound(assetId: string): Promise<unknown> {
    const hasSound = this.assetCollection.hasSoundExists(assetId)
    if (!hasSound) {
      throw new MiniAppCoreError('NOT_FOUND_ASSET_ID')
    } else {
      return this.bridge.stopSound(assetId)
    }
  }

  /**
   * turn on the sound
   * @group Hardware Invoked Methods
   *
   * @example
   * ```ts
   * await window.MiniAppSDK.soundOn();
   * ```
   */
  public static async soundOn(): Promise<void> {
    return this.bridge.soundOn()
  }

  /**
   * turn off the sound
   * @group Hardware Invoked Methods
   *
   * @example
   * ```ts
   * await window.MiniAppSDK.soundOff();
   * ```
   */
  public static async soundOff(): Promise<void> {
    return this.bridge.soundOff()
  }

  /**
   * send the mobile version compatibility with Javascript SDK to cross check backward compat
   *
   * @group General methods
   *
   * @example
   * ```ts
   * // {"os": "iOS", "version": "3.0.0"}
   * const checkCompatibility = await window.MiniAppSDK.checkCompatibility();
   * console.log(checkCompatibility);
   *
   */
  public static async checkCompatibility(): Promise<void> {
    // Todo: this function return object ({"os": "iOS", "version": "3.0.0"})
    return this.bridge.checkCompatibility()
  }

  /**
   * Gets an authentication token for the user.
   *
   * @group General methods
   * @param {@link appId}
   *
   * @example
   * ```ts
   * const exchangeToken = await window.MiniAppSDK.exchangeToken(appId);
   * console.log(exchangeToken);
   * ```
   */
  static async exchangeToken(
    forceRefresh = false
  ): Promise<ExchangeTokenPayload> {
    if (
      !forceRefresh &&
      this.exchangeTokenData &&
      this.exchangeTokenJWT?.payload.exp
    ) {
      const exp = +this.exchangeTokenJWT.payload.exp
      const now = Math.floor(Date.now() / 1000)
      const tokenExpTimeInSeconds = exp - now

      if (tokenExpTimeInSeconds > AppConfig.TOKEN_EXPIRY_THRESHOLD) {
        return this.exchangeTokenData
      }
    }

    this.exchangeTokenData = await this.bridge.exchangeToken()
    if (this.exchangeTokenData) {
      this.exchangeTokenJWT = decodeJWT(this.exchangeTokenData.accessToken)
    }

    return this.exchangeTokenData
  }

  /**
   * Accept tutorial
   *
   * @group General methods
   *
   * @example
   * ```ts
   * await window.MiniAppSDK.acceptTutorial();
   * ```
   *
   */
  public static async acceptTutorial() {
    return this.bridge.acceptTutorial()
  }

  /**
   * Cancel Tutorial
   *
   * @group General methods
   *
   * @example
   * ```ts
   * await window.MiniAppSDK.cancelTutorial();
   * ```
   */
  public static async cancelTutorial() {
    return this.bridge.cancelTutorial()
  }

  /**
   * Checks if a specific permission is granted.
   *
   * @param permissionType - The type of permission to check.
   * @returns A Promise resolving to a boolean indicating whether the permission is granted.
   */
  public static async isPermissionAllowed(
    permissionType: PermissionType
  ): Promise<boolean> {
    return this.bridge.isPermissionAllowed(permissionType)
  }

  /**
   * Requests a specific permission from the user.
   *
   * @param permissionType - The type of permission to request.
   * @returns A Promise resolving to a boolean indicating whether the permission was granted.
   */
  public static async requestPermission(
    permissionType: PermissionType
  ): Promise<undefined> {
    return this.bridge.requestPermission(permissionType)
  }

  /**
   * Set a callback function when mobile active the miniapp
   * @group Hardware Invoked Methods
   *
   * @example
   * ```ts
   * window.MiniAppSDK.onAppActive(() => {
   *   window.MiniAppSDK.soundOn();
   * });
   * ```
   */
  public static onAppActive(callbackFunction: Function) {
    const event: BindNativeEvents = 'NativeApp.active'
    this.mobileEvents.set(event, callbackFunction)
  }

  /**
   * Set a callback function when mobile inactive the miniapp
   * @group Hardware Invoked Methods
   *
   * @example
   * ```ts
   * window.MiniAppSDK.onAppInActive(() => { window.MiniAppSDK.soundOff() });
   * ```
   */
  public static onAppInActive(callbackFunction: Function) {
    const event: BindNativeEvents = 'NativeApp.inactive'
    this.mobileEvents.set(event, callbackFunction)
  }

  /**
   * Registers a callback function to be invoked when the Android hardware back button is pressed.
   * @group Hardware Invoked Methods
   *
   * @example
   * ```ts
   * window.MiniAppSDK.onHardwareBack(() => {
   *   // Perform an action when the back button is pressed
   * });
   * ```
   */
  public static onHardwareBack(callbackFunction: Function) {
    const event: BindNativeEvents = 'NativeApp.android.onHardwareBack'
    this.mobileEvents.set(event, callbackFunction)
  }

  /**
   * This function is called by native for mounted and unmounted communication
   * @hidden
   */
  public static async mobileEventTrigger(mobileEventData: string) {
    let mobileEvent!: MobileEvent
    try {
      mobileEvent = JSON.parse(mobileEventData) as MobileEvent
    } catch (error) {
      new BridgeError('DATA_IS_NOT_VALID_JSON_STRING')
    }

    const callbackFunction = this.mobileEvents.get(
      mobileEvent.event
    ) as Function

    if (callbackFunction) {
      await callbackFunction(mobileEvent)
    }
  }

  /**
   * tell the native application to load assets in background.
   *
   * @param {@link AssetPayload}
   *
   * @group General methods
   *
   * @example
   * ```ts
   * await window.MiniAppSDK.preloadAssets({ sound: soundAssets });
   * ```
   *
   */
  public static async preloadAssets(
    payload: AssetPayload
  ): Promise<AssetPayload> {
    if (isEmpty(payload)) throw new MiniAppCoreError('NOT_FOUND_PAYLOAD')

    const schema: yup.SchemaOf<AssetPayload> = yup.object({
      sound: yup
        .array()
        .of(
          yup.object({
            id: yup.string().required(),
            url: yup.string().required(),
          })
        )
        .notRequired(),
      lottie: yup
        .array()
        .of(
          yup.object({
            id: yup.string().required(),
            url: yup.string().required(),
          })
        )
        .notRequired(),
      model3d: yup
        .array()
        .of(
          yup.object({
            id: yup.string().required(),
            url: yup.string().required(),
          })
        )
        .notRequired(),
      texture2d: yup
        .array()
        .of(
          yup.object({
            id: yup.string().required(),
            url: yup.string().required(),
          })
        )
        .notRequired(),
      image: yup
        .array()
        .of(
          yup.object({
            id: yup.string().required(),
            url: yup.string().required(),
          })
        )
        .notRequired(),
    })
    await PayloadValidator.validate(schema, payload)
    const assets = await this.assetCollection.add(payload)
    // Currently, all asset categories are required for mobile devices, which is why we've added default values for each category
    await this.bridge.preloadAssets({
      sound: [],
      lottie: [],
      model3d: [],
      texture2d: [],
      image: [],
      ...payload,
    })
    return assets
  }

  /**
   * tell the native application to check preload status
   *
   * @group General methods
   *
   *
   * @example
   * ```ts
   * // idle
   * const checkPreloadAssetsStatus = await window.MiniAppSDK.checkPreloadAssetsStatus();
   * console.log(checkPreloadAssetsStatus);
   * ```
   *
   */
  public static checkPreloadAssetsStatus(): Promise<PreloadAssetsStatus> {
    return this.bridge.checkPreloadAssetsStatus()
  }

  /**
   * Get store information.
   * @group Services Methods
   */
  public static async getStoresNearBy(
    lat: number,
    lng: number,
    rad: number
  ): Promise<StoreInfo[]> {
    const schema: yup.SchemaOf<StoresNearByParameter> = yup.object({
      lat: yup.number().required(),
      lng: yup.number().required(),
      rad: yup.number().notRequired(),
    })
    await PayloadValidator.validate(schema, { lat, lng, rad })

    return this.allmapService.getStoresNearBy(lat, lng, rad).catch((error) => {
      throw new MiniAppCoreError("GET_STORES_ERROR")
    })
  }

  /**
   * Open register customer page in seven app.
   * @group App Invoked Methods
   *
   *   * @example
   * ```ts
   * let navPage = "SE006"
   * let subNavPage = "SE013"
   * let additionalData = {
   *  pictureUrl: "image.url"
   * }
   * await window.MiniAppSDK.navigateByNavPage(navPage, subNavPage, additionalData);
   * ```
   */
  public static async navigateByNavPage(
    navPage: string,
    subNavPage: string | null,
    additionalData: any
  ): Promise<void> {
    return this.bridge.navigateByNavPage(navPage, subNavPage, additionalData)
  }

  /**
   * Get 7-11 mobile application latest login provider.
   * @group App Invoked Methods
   *
   *   * @example
   * ```ts
   * await window.MiniAppSDK.getLatestLoginProvider();
   * ```
   */
  public static async getLatestLoginProvider(): Promise<LoginProvider> {
    const { latestLoginProvider } = await this.bridge.getLatestLoginProvider()
    const provider = Object.values(LoginProvider).find(
      (provider) => provider === latestLoginProvider
    )
    return provider ?? LoginProvider.UNKNOWN
  }

  /**
   * Open PDPA page in seven app.
   * @group PDPA
   *
   *   * @example
   * ```ts
   * const payload = {
   *  activityId: "AT123456"
   * }
   * await window.MiniAppSDK.openPDPA(payload);
   * ```
   */
  public static async openPDPA(payload: OpenPDPAPayload): Promise<OpenPDPAResponse> {
    return this.bridge.openPDPA(payload)
  }
}

/**
 * Class representing MiniAppSDK.
 * This class has no constructor, could not be initialized by `new` operator.
 * calling this class by using MiniAppSDK.ARScene[functionName]
 * @class
 */
class ARScene extends MiniAppSDK {
  private static isStartAR: boolean = false
  /**
   * delete 3d model
   *
   * @param {@link modelId}
   *
   * @group Hardware Invoked Methods
   */
  public static async deleteModel(modelId: string): Promise<void> {
    const hasModel = this.arSceneConfiguration.modelExists(modelId)
    if (!hasModel) {
      throw new MiniAppCoreError('NOT_FOUND_MODEL_ID')
    } else {
      this.bridge.deleteModel(modelId)
    }
  }

  /**
   * Tell the Native application to start an AR scene using a scene configuration
   *
   * @group Hardware Invoked Methods
   */
  public static async start(payload: StartARScenePayload): Promise<void> {
    if (this.isStartAR)
      throw new MiniAppARSceneError('AR_SCENE_ALREADY_STARTED')
    const schema: yup.SchemaOf<StartARScenePayload> = yup.object({
      anchor: yup.mixed().oneOf(['random-distribution']),
      title: yup.string().required(),
      objectDistributionScale: yup.number().required(),
      userLeaveOriginOverlay: yup
        .object({
          elements: yup
            .array()
            .of(
              yup
                .object({
                  type: yup.mixed().oneOf(['image', 'text']).required(),
                  text: yup.string().notRequired(),
                  size: yup.string().notRequired(),
                  weight: yup.mixed().oneOf(['normal', 'bold']).notRequired(),
                  color: yup.string().notRequired(),
                  image: yup.string().notRequired(),
                })
                .required()
            )
            .required(),
          distanceFromOrigin: yup.number().required(),
          warning: yup
            .object({
              threshold: yup.number().max(1).required(),
              elements: yup
                .array()
                .of(
                  yup
                    .object({
                      type: yup.mixed().oneOf(['image', 'text']).required(),
                      text: yup.string().notRequired(),
                      size: yup.string().notRequired(),
                      weight: yup
                        .mixed()
                        .oneOf(['normal', 'bold'])
                        .notRequired(),
                      color: yup.string().notRequired(),
                      image: yup.string().notRequired(),
                    })
                    .required()
                )
                .required(),
            })
            .notRequired(),
        })
        .notRequired(),
      targetTaps: yup.string().notRequired(),
      model: yup
        .object({
          animation: yup
            .object({
              isEnabled: yup.bool().notRequired(),
              type: yup.mixed().oneOf(['shuffle']).notRequired(),
              targetModelIds: yup.array().notRequired(),
              winnerModelId: yup.string().notRequired(),
              duration: yup.number().notRequired(),
            })
            .notRequired(),
          data: yup
            .array()
            .of(
              yup.object({
                id: yup.string().required(),
                name: yup.string().notRequired(),
                model3d: yup.string().required(),
                texture2d: yup.object({
                  baseColor: yup.string().notRequired(),
                }),
                imageId: yup.string().notRequired(),
              })
            )
            .required(),
        })
        .required(),
      onTabModel: yup.mixed().notRequired(),
      onLeaveScene: yup.mixed().notRequired(),
      onMounted: yup.mixed().notRequired(),
      onTimeout: yup.mixed().notRequired(),
      onAnimationFinished: yup.mixed().notRequired(),
      footer: yup
        .object({
          background: yup.string().notRequired(),
          style: yup
            .object({
              elementsMarginTop: yup.string().notRequired(),
              elementsMarginBottom: yup.string().notRequired(),
            })
            .notRequired(),
          elements: yup.array().of(
            yup
              .object({
                type: yup.mixed().oneOf(['image', 'text']).required(),
                text: yup.string().notRequired(),
                size: yup.string().notRequired(),
                weight: yup.mixed().oneOf(['normal', 'bold']).notRequired(),
                color: yup.string().notRequired(),
                image: yup.string().notRequired(),
              })
              .notRequired()
          ),
        })
        .notRequired(),
      footerInfluencer: yup.object({
        influencerImageId: yup.string().notRequired(),
        isMediaCapturePreventedByInfluencer: yup.boolean().notRequired(),
        bottomImageId: yup.string().notRequired(),
        imageCardImageId: yup.string().notRequired(),
        textCardImageId: yup.string().notRequired(),
        messageBubbleLottieId: yup.string().notRequired(),
        progressCardImageId: yup.string().notRequired(),
      }),
      overlays: yup
        .array()
        .of(
          yup.object({
            id: yup.string().required(),
            loop: yup.number().required(),
            alignment: yup
              .mixed()
              .oneOf(['fit-screen', 'fill-screen'])
              .required(),
          })
        )
        .notRequired(),
      timer: yup
        .object({
          isEnabled: yup.boolean().notRequired(),
          duration: yup.number().notRequired(),
          timerLottieId: yup.string().notRequired(),
          timeoutOverlay: yup.object({
            elements: yup
              .array()
              .of(
                yup
                  .object({
                    type: yup.mixed().oneOf(['image', 'text']).required(),
                    text: yup.string().notRequired(),
                    size: yup.string().notRequired(),
                    weight: yup.mixed().oneOf(['normal', 'bold']).notRequired(),
                    color: yup.string().notRequired(),
                    image: yup.string().notRequired(),
                  })
                  .required()
              )
              .notRequired(),
          }),
        })
        .notRequired(),
    })
    await PayloadValidator.validate(schema, payload)
    const configuration = this.arSceneConfiguration.configure(payload)
    if (payload?.onLeaveScene) {
      this.mobileEvents.set(
        'NativeApp.ARScene.onLeaveScene',
        payload.onLeaveScene
      )
    }

    if (payload?.onTabModel) {
      this.mobileEvents.set('NativeApp.ARScene.onTabModel', payload.onTabModel)
    }

    if (payload?.onMounted) {
      this.mobileEvents.set('NativeApp.ARScene.onMounted', payload.onMounted)
    }

    if (payload?.onTimeout) {
      this.mobileEvents.set('NativeApp.ARScene.onTimeout', payload.onTimeout)
    }

    if (payload?.onAnimationFinished) {
      this.mobileEvents.set(
        'NativeApp.ARScene.onAnimationFinished',
        payload.onAnimationFinished
      )
    }
    const result = await this.bridge.startAR(configuration)
    this.isStartAR = true
    return result
  }

  /**
   * Tell the native application to end the current AR scene
   *
   * @group Hardware Invoked Methods
   */
  public static async end(): Promise<void> {
    if (!this.isStartAR)
      throw new MiniAppARSceneError('AR_SCENE_HAS_NOT_STARTED_YET')
    const result = await this.bridge.endAR()
    this.isStartAR = false
    this.arSceneConfiguration.clearData()
    return result
  }

  /**
   * Get ARScene status
   */
  public static getARSceneStatus(): boolean {
    return this.isStartAR
  }

  /**
   * On-Demand Overlay
   *
   * @param {@link ShowOverlayPayload}
   *
   * @group General methods
   */
  public static async showOverlay(payload: ShowOverlayPayload): Promise<void> {
    if (!this.isStartAR)
      throw new MiniAppARSceneError('AR_SCENE_HAS_NOT_STARTED_YET')
    const schema: yup.SchemaOf<ShowOverlayPayload> = yup.object({
      id: yup.string().required(),
      loop: yup.number().required(),
      alignment: yup.mixed().oneOf(['fit-screen', 'fill-screen']).required(),
    })
    await PayloadValidator.validate(schema, payload)
    const hasLottie = this.assetCollection.hasLottieExists(payload.id)
    if (!hasLottie) {
      throw new MiniAppCoreError('NOT_FOUND_ASSET_ID')
    } else {
      return this.bridge.showOverlay(payload)
    }
  }

  /**
   * Update the content rendered footer on ar
   *
   * @param {ARSceneContent} payload - The ARSceneContent object containing the progress, title, and subtitle information.
   *
   * @throws MiniAppARSceneError - If AR scene has not been started yet.
   *
   * @group General methods
   */
  public static async updateARSceneContent(payload: ARSceneContent) {
    if (!this.isStartAR)
      throw new MiniAppARSceneError('AR_SCENE_HAS_NOT_STARTED_YET')

    const schema: yup.SchemaOf<ARSceneContent> = yup.object({
      progress: yup.string().notRequired(),
      title: yup.string().notRequired(),
      subTitle: yup.string().notRequired(),
      imageId: yup.string().notRequired(),
    })
    await PayloadValidator.validate(schema, payload)

    return this.bridge.updateARSceneContent(payload)
  }
}

MiniAppSDK.ARScene = ARScene

export default MiniAppSDK
